/**
 * What the Showcase lists, and how its filters read (KPR-1923, KPE-062).
 *
 * The data is the compiled video manifest (KPR-1921: docs/videos/*.md, built
 * into public/docs-portal/videos.json) and, for the How-to row's order, the
 * guides index (guides.json). Nothing here renders: the page
 * (ShowcaseList.js) and the build-time prerender (scripts/prerender-public.mjs)
 * both list videos through these functions, so the HTML a link preview reads
 * and the page a person sees name the same videos.
 */
/**
 * The page's words for whoever reads the HTML without running it: the tab,
 * search results and a link preview. Its address is permanent: every post
 * links here. The prerender gives it the landing page's preview image
 * (scripts/prerender-public.mjs); this module imports nothing, so the page's
 * chunk carries no more than it reads.
 */
export const SHOWCASE_META = Object.freeze({
	path: '/docs/showcase',
	title: 'Showcase',
	description: 'Short videos of keepr at work: at home, for a project or hobby, and for a business.',
});

/** Who a video is for: the manifest's `audience` values, in the words of the messaging. */
export const AUDIENCES = Object.freeze([
	Object.freeze({ key: 'home', label: 'At home' }),
	Object.freeze({ key: 'project', label: 'For a project or hobby' }),
	Object.freeze({ key: 'business', label: 'For a business' }),
]);

/** The capability groups of docs/videos/capabilities.yaml, as the Shows filter heads them. */
export const SHOWS_GROUPS = Object.freeze({
	capture: 'Capture',
	find: 'Find',
	trust: 'Trust',
	run: 'Run',
	start: 'Start',
});

/** The query parameters a filtered view's address carries. */
export const FILTER_PARAMS = Object.freeze({ audience: 'for', shows: 'shows' });

/** A video's own page (KPR-1924). */
export const videoPath = key => `${SHOWCASE_META.path}/${encodeURIComponent(key)}`;

/**
 * The videos a page may list: every manifest video but the test fixture
 * (`fixture: true`), which is listed only outside a production build, for
 * local runs and tests. The prerender runs as production, so it never lists it.
 */
export function listedVideos(index, { fixtures = process.env.NODE_ENV !== 'production' } = {}) {
	const videos = Array.isArray(index?.videos) ? index.videos : [];
	return videos.filter(video => video && typeof video.key === 'string' && (fixtures || video.fixture !== true));
}

/** The instructional videos go in the How-to row; everything else is a clip. */
export const isHowto = video => video?.format === 'howto';

const listOf = value => (Array.isArray(value) ? value : []);

/** One query parameter's values: comma-separated, any case, repeated or not, kept only when known, in `known`'s order. */
function readValues(searchParams, name, known) {
	const given = new Set(
		searchParams.getAll(name)
			.flatMap(value => value.split(','))
			.map(value => value.trim().toLowerCase())
			.filter(Boolean)
	);
	return known.filter(key => given.has(key));
}

/**
 * The filters a URL asks for: `?for=home,business&shows=item-form`. A value
 * that is not an audience or a capability is ignored, so an old or mangled
 * link still opens the page.
 */
export function readFilters(searchParams, capabilities = []) {
	return {
		audience: readValues(searchParams, FILTER_PARAMS.audience, AUDIENCES.map(a => a.key)),
		shows: readValues(searchParams, FILTER_PARAMS.shows, listOf(capabilities).map(c => c.key)),
	};
}

/**
 * The URL's query for a set of filters, the page's other parameters kept.
 * An empty filter leaves its parameter out, so the unfiltered page's address
 * is the bare path.
 */
export function filtersToParams(filters, searchParams = new URLSearchParams()) {
	const next = new URLSearchParams(searchParams);
	[['audience', FILTER_PARAMS.audience], ['shows', FILTER_PARAMS.shows]].forEach(([field, name]) => {
		next.delete(name);
		if (filters[field]?.length) next.set(name, filters[field].join(','));
	});
	return next;
}

/** Any of the chosen audiences, and any of the chosen capabilities; a filter with nothing chosen lets everything through. */
export function matches(video, filters) {
	const audience = listOf(video.audience);
	const shows = listOf(video.shows);
	const audienceOk = !filters.audience.length || filters.audience.some(key => audience.includes(key));
	const showsOk = !filters.shows.length || filters.shows.some(key => shows.includes(key));
	return audienceOk && showsOk;
}

export const isFiltered = filters => Boolean(filters.audience.length || filters.shows.length);

/**
 * The Shows filter's choices: the capabilities the listed videos show (a
 * choice that matches nothing is not offered), plus any chosen in the URL, so
 * a chosen one can always be taken off. Grouped as capabilities.yaml groups
 * them, in its order.
 */
export function showsOptions(videos, capabilities = [], chosen = []) {
	const shown = new Set(videos.flatMap(video => listOf(video.shows)));
	chosen.forEach(key => shown.add(key));
	const groups = [];
	listOf(capabilities).forEach(capability => {
		if (!shown.has(capability.key)) return;
		let group = groups.find(entry => entry.key === capability.group);
		if (!group) {
			group = { key: capability.group, label: SHOWS_GROUPS[capability.group] || capability.group, options: [] };
			groups.push(group);
		}
		group.options.push({ value: capability.key, label: capability.label });
	});
	return groups;
}

/**
 * The How-to row's order: the order of their guides in the documentation —
 * section by section as the guides index lists them, then each guide's place
 * in its section. A how-to with no guide anyone can read (none named, or one
 * not in the public index) comes last, by title.
 */
export function howtoOrder(videos, guidesIndex) {
	const sections = listOf(guidesIndex?.sections).map(section => section.key);
	const guides = new Map(listOf(guidesIndex?.guides).map(guide => [guide.id, guide]));
	const placeOf = video => {
		const guide = video.guide ? guides.get(video.guide) : null;
		if (!guide) return null;
		const section = sections.indexOf(guide.section);
		return { section: section === -1 ? sections.length : section, order: Number(guide.order) || 0, slug: String(guide.slug || '') };
	};
	const byTitle = (a, b) => String(a.title).localeCompare(String(b.title)) || a.key.localeCompare(b.key);
	return videos
		.map(video => ({ video, place: placeOf(video) }))
		.sort((a, b) => {
			if (!a.place || !b.place) return (a.place ? -1 : b.place ? 1 : 0) || byTitle(a.video, b.video);
			return a.place.section - b.place.section
				|| a.place.order - b.place.order
				|| a.place.slug.localeCompare(b.place.slug)
				|| byTitle(a.video, b.video);
		})
		.map(entry => entry.video);
}

/** The clips in the manifest's order (by key, as the build compiles them). */
export const clipsOf = videos => videos.filter(video => !isHowto(video));

/** Everything the page shows for one set of filters: the clips, then the How-to row. */
export function showcaseSections(videos, guidesIndex, filters) {
	const kept = videos.filter(video => matches(video, filters));
	return { clips: clipsOf(kept), howto: howtoOrder(kept.filter(isHowto), guidesIndex) };
}

// ---- A video's own page (KPR-1924) ----

/**
 * The Showcase list's order, unfiltered: the clips, then the How-to row. A
 * video's page steps through it with Previous and Next, as a guide steps
 * through its section.
 */
export function showcaseOrder(videos, guidesIndex) {
	const { clips, howto } = showcaseSections(videos, guidesIndex, { audience: [], shows: [] });
	return [...clips, ...howto];
}

/** The videos before and after `key` in `order`: { previous, next }, either null. */
export function neighbours(order, key) {
	const at = listOf(order).findIndex(video => video.key === key);
	if (at === -1) return { previous: null, next: null };
	return { previous: order[at - 1] || null, next: order[at + 1] || null };
}

/** A guide's page, by its id (`section/slug`). */
export const guidePath = id => `/docs/guides/${String(id).split('/').map(encodeURIComponent).join('/')}`;

/** A Discover package's page, by its key (`publisher/name` keeps its slash). */
export const packagePath = key => `/discover/${String(key).split('/').map(encodeURIComponent).join('/')}`;

/**
 * Where Try it goes: the package the video shows, on Discover; else the guide
 * it teaches; else nowhere (no button). { to, kind } or null.
 */
export function tryItTarget(video) {
	if (video?.package) return { to: packagePath(video.package), kind: 'package' };
	if (video?.guide) return { to: guidePath(video.guide), kind: 'guide' };
	return null;
}

/** The places a clip is also posted, in this order, with the names people know them by. */
export const SOCIAL_SITES = Object.freeze([
	Object.freeze({ key: 'instagram', label: 'Instagram' }),
	Object.freeze({ key: 'tiktok', label: 'TikTok' }),
	Object.freeze({ key: 'youtube', label: 'YouTube' }),
	Object.freeze({ key: 'linkedin', label: 'LinkedIn' }),
]);

const isWebAddress = value => {
	if (typeof value !== 'string') return false;
	try {
		return new URL(value).protocol === 'https:';
	} catch {
		return false;
	}
};

/**
 * The manifest's `social` links as plain outbound links: [{ key, label, href }].
 * Only an https address is kept; nothing is ever loaded from those sites.
 */
export function socialLinks(video) {
	const social = video?.social && typeof video.social === 'object' ? video.social : {};
	return SOCIAL_SITES
		.filter(site => isWebAddress(social[site.key]))
		.map(site => ({ ...site, href: social[site.key] }));
}

/** The audience labels of a video, in AUDIENCES' order. */
export const audienceLabels = video => AUDIENCES.filter(a => listOf(video?.audience).includes(a.key)).map(a => a.label);

/** The Shows labels of a video, in its own order; a key with no label is left out. */
export function showsLabels(video, capabilities = []) {
	const byKey = new Map(listOf(capabilities).map(c => [c.key, c.label]));
	return listOf(video?.shows).map(key => byKey.get(key)).filter(Boolean);
}

/**
 * A video page's words for whoever reads the HTML without running it: the
 * tab, search results and a link preview. The poster is the preview image;
 * `origin` makes a relative one absolute.
 */
export function videoPageMeta(video, origin = '') {
	const poster = String(video?.poster || '');
	const image = /^https?:\/\//.test(poster) ? poster : `${origin}${poster}`;
	return {
		path: videoPath(video.key),
		title: video.title,
		description: video.valueLine || SHOWCASE_META.description,
		image,
		imageAlt: video.title,
	};
}
